import React, { useState, useContext, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { BsHeart, BsHeartFill, BsX, BsCheckCircleFill } from "react-icons/bs";
import { AiOutlineShoppingCart } from "react-icons/ai";
import Swal from "sweetalert2";
import "../../../assets/css/customer/subscreens/account/AddtoCart.css";
import MyNavbar from "../../../components/general/mynavbar/MyNavbar";
import useScrollToTop from "../../../contexts/UseScrolltoTop";
import LoadingScreen from "../../../components/general/animations/Animations.js";
import axios from "axios";
import AppContext from "../../../contexts/AppContext";
import { Badge } from "react-bootstrap";
import "react-toastify/dist/ReactToastify.css";

// ── Address validation ───────────────────────────────────────────
// Uses AppContext's regExpChecker("required", ...) to detect an empty
// field, then shows a field-specific label, then a format rule on top.
const validateAddress = (fullName, phone, address, regExpChecker) => {
  const errors = {};

  if (regExpChecker("required", fullName)) {
    errors.name = "Full Name is required";
  } else if (fullName.trim().length < 3) {
    errors.name = "Full name kam az kam 3 characters ka hona chahiye";
  }

  if (regExpChecker("required", phone)) {
    errors.phone = "Phone Number is required";
  } else {
    const cleanPhone = phone.replace(/[-\s]/g, "");
    if (!/^[0-9]{10,13}$/.test(cleanPhone)) {
      errors.phone = "Valid phone number enter karein (10-13 digits)";
    }
  }

  if (regExpChecker("required", address)) {
    errors.area = "Street / Area is required";
  } else if (address.trim().length < 5) {
    errors.area = "Pura address likhein (street, area, etc.)";
  }

  return errors;
};

// ════════════════════════════════════════════════════════════════
const AddToCart = () => {
  const {
    baseUrl,
    baseUrlForFiles,
    authToken,
    customerAuthToken,
    customer,
    clearUnread,
    incrementUnread,
    setSelectedProductForCustomer, // used to open ProductDetail with this product
    regExpChecker,
  } = useContext(AppContext);
  useScrollToTop();
  const navigate = useNavigate();
  const location = useLocation();
  const incomingItem = location.state?.newItem;

  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [addressErrors, setAddressErrors] = useState({});
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  // Ref to the "Delivery Address" section inside the checkout modal so we
  // can auto-scroll it into view whenever validation fails.
  const addressSectionRef = useRef(null);
  const nameInputRef = useRef(null);

  const promoCodes = {
    SAVE10: { discount: 10, type: "percentage" },
    FLAT300: { discount: 300, type: "fixed" },
    WELCOME20: { discount: 20, type: "percentage" },
  };

  const activeCartItems = cartItems;
  const selectedItems = activeCartItems;

  const subtotal = selectedItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const shippingFee = selectedItems.length > 0 ? 200 : 0;
  const discount = (() => {
    if (!appliedPromo || subtotal === 0) return 0;
    return appliedPromo.type === "percentage"
      ? Math.round((subtotal * appliedPromo.discount) / 100)
      : appliedPromo.discount;
  })();
  const total = subtotal + shippingFee - discount;

  const viewCart = () => {
    const formdata = new FormData();
    formdata.append("chcustomer_id", customer?.id);

    axios
      .post(`${baseUrl}/customer/cart/viewCart`,formdata, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Authorization": `Bearer ${customerAuthToken}`,
      },})
      .then((response) => {
        console.log("Cart Data:", response.data);
        setCartItems(response.data.carts);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setLoading(false);
          });
        });
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

 const deleteCart = (id) => {
  const formdata = new FormData();
  formdata.append("chcart_id", id);

  axios
    .post(`${baseUrl}/customer/cart/deleteCart`, formdata, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Authorization": `Bearer ${customerAuthToken}`,
      },
    })
    .then((response) => {
      console.log(response.data);
      viewCart();
    })
    .catch((error) => console.log(error));
};

// ── Navigate to full Product Detail page for this cart item ──
const handleViewProduct = (item) => {
  if (!item?.product) return;
  setSelectedProductForCustomer(item.product);
  navigate("/product"); // replace with your actual product detail route
};

// ── Proceed to Checkout handler ──────────────────────────────
// Validates the address, and on failure:
//   1) resets isPlacingOrder so the button never gets stuck, and
//   2) smooth-scrolls the modal so the Delivery Address section
//      slides into view automatically.
const createOrder = () => {
  if (isPlacingOrder) return;
  setIsPlacingOrder(true);

  const errors = validateAddress(fullName, phone, address, regExpChecker);
  if (Object.keys(errors).length > 0) {
    setAddressErrors(errors);
    setIsPlacingOrder(false);

    if (addressSectionRef.current) {
      addressSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    if (errors.name && nameInputRef.current) {
      nameInputRef.current.focus();
    }
    return;
  }
  setAddressErrors({});

  const savedCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));
  const sellerId = cartItems[0]?.product?.seller_id;

  const formdata = new FormData();
  formdata.append("chcustomer_id", savedCustomer?.id);
  formdata.append("seller_id", sellerId);
  formdata.append("total_amount", total);
  formdata.append("shipping_fee", shippingFee);
  formdata.append("payment_method", "COD");
  formdata.append("full_name", fullName);
  formdata.append("phone", phone);
  formdata.append("address", address);

  axios
    .post(`${baseUrl}/customer/order/createOrder`, formdata, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Authorization": `Bearer ${customerAuthToken}`,  
      },
    })
    .then(() => {
      setShowModal(false);
      incrementUnread("notifications");
      setShowSuccess(true);
    })
    .catch(() => {
      toast.error("Order failed. Try again.");
    })
    .finally(() => setIsPlacingOrder(false));
};

  useEffect(() => {
    viewCart();
    clearUnread("cart");
  }, []);

  if (loading) {
    return <LoadingScreen type="loading" />;
  }

  if (showSuccess) {
    return (
      <LoadingScreen type="success" onComplete={() => navigate("/orders")} />
    );
  }

  return (
    <>
      <ToastContainer />
      <MyNavbar />
      <div className="cart-page">
        <div className="cart-container">
          <h1 className="cart-title">Your Cart</h1>
          <p className="cart-subtitle">Review your items and proceed to checkout</p>

          {activeCartItems.length === 0 ? (
            <div
              className="empty-cart"
              style={{ opacity: 1, transform: "none" }}
            >
              <AiOutlineShoppingCart size={80} className="empty-cart-icon" />
              <h2>Your cart is empty</h2>
              <p>Add some items to get started!</p>
              <button
                className="continue-shopping-btn"
                onClick={() => navigate("/categories")}
              >
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="cart-content">
              {/* ── LEFT: Items ──────────────────────────── */}
              <div className="cart-items-section">
                {activeCartItems.map((item) => {
                  const imgSrc =
                    item.product?.images?.length > 0
                      ? `${baseUrlForFiles}/${item.product.images[0].image_path}`
                      : "/placeholder.png";

                  const productName =
                    item.product?.product_name || `Product #${item.product_id}`;

                  return (
                    <div
                      key={item.id}
                      className="cart-item-card"
                      onClick={() => handleViewProduct(item)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="cart-item-image">
                        <img src={imgSrc} alt={productName} />
                      </div>

                      <div className="cart-item-details">
                        <h3 className="cart-item-name">
                          {productName}
                          {item.isCustomized == 1 && (
                            <span className="customized-badge">Customized</span>
                          )}
                        </h3>
                        <p className="cart-item-price">
                          Rs {item.price?.toLocaleString()}
                        </p>

                        <div className="cart-item-variants">
                          {item.size &&
                            item.size !== "null" &&
                            item.size !== "" && <span>Size: {item.size}</span>}

                          {item.product?.variant?.colors &&
                            item.product.variant.colors.trim() !== "" && (
                              <span className="color-variant">
                                Color:
                                <span
                                  className="color-dot"
                                  style={{
                                    backgroundColor:
                                      item.product.variant.colors.split(",")[
                                        item.color
                                      ] ?? "transparent",
                                  }}
                                />
                              </span>
                            )}
                        </div>

                        {item.stockCount != null && item.stockCount <= 5 && (
                          <p className="stock-warning">
                            Limited stock — Only {item.stockCount} left!
                          </p>
                        )}

                        <div className="cart-item-quantity">
                          <span className="color-variant">
                            Quantity:&nbsp;&nbsp;&nbsp;&nbsp;
                            {item.quantity}
                          </span>
                        </div>
                      </div>

                      <button
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCart(item.id);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* ── RIGHT: Order Summary ─────────────────── */}
              <div className="order-summary-section">
                <div className="order-summary-card">
                  <h2 className="summary-title">Order Summary</h2>

                  {selectedItems.length === 0 ? (
                    <p className="no-item-selected">
                      Select items to see summary
                    </p>
                  ) : (
                    <>
                      <div className="summary-row">
                        <span>
                          Subtotal ({selectedItems.length} item
                          {selectedItems.length > 1 ? "s" : ""})
                        </span>
                        <span>Rs {subtotal.toLocaleString()}</span>
                      </div>
                      <div className="summary-row">
                        <span>Shipping Fee</span>
                        <span>Rs {shippingFee}</span>
                      </div>
                      {discount > 0 && (
                        <div className="summary-row discount-row">
                          <span>Discount</span>
                          <span>- Rs {discount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="summary-divider" />
                      <div className="summary-row total-row">
                        <span>Total</span>
                        <span>Rs {total.toLocaleString()}</span>
                      </div>
                    </>
                  )}

                  {/* <div className="promo-section">
                    <h3 className="promo-title">Promo Code</h3>
                    {appliedPromo ? (
                      <div className="applied-promo">
                        <span className="promo-code-text">
                          {promoCode.toUpperCase()}
                        </span>
                        <button className="remove-promo-btn">
                          <BsX size={20} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="promo-input-group">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            placeholder="Enter Coupon Code"
                            className="promo-input"
                          />
                          <button className="apply-btn">Apply</button>
                        </div>
                        {promoError && (
                          <p className="promo-error">{promoError}</p>
                        )}
                      </>
                    )}
                  </div> */}

                  <button
                    className="checkout-btn"
                    onClick={() => setShowModal(true)}
                  >
                    {`Buy Now (${selectedItems.length})`}
                  </button>
                  <button
                    className="continue-shopping-btn"
                    onClick={() => navigate("/categories")}
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════
          CHECKOUT MODAL
      ════════════════════════════════════════════════ */}
      {showModal && (
        <div className="cm-backdrop" onClick={() => setShowModal(false)}>
          <div className="cm-box" onClick={(e) => e.stopPropagation()}>
            <div className="cm-header">
              <h2 className="cm-title">Checkout</h2>
              <button className="cm-close" onClick={() => setShowModal(false)}>
                <BsX size={24} />
              </button>
            </div>

            <div className="cm-body">
              <div className="cm-section">
                <h3 className="cm-section-title">Your Items</h3>
                <div className="cm-items-list">
                  {selectedItems.map((item) => {
                    const imgSrc =
                      item.product?.images?.length > 0
                        ? `${baseUrlForFiles}/${item.product.images[0].image_path}`
                        : "/placeholder.png";
                    const productName =
                      item.product?.product_name ||
                      `Product #${item.chproduct_id}`;
                    return (
                      <div key={item.id} className="cm-item-row">
                        <img
                          src={imgSrc}
                          alt={productName}
                          className="cm-item-img"
                        />
                        <div className="cm-item-info">
                          <p className="cm-item-name">
                            {productName}
                            {item.isCustomized == 1 && (
                              <span className="customized-badge">
                                Customized
                              </span>
                            )}
                          </p>
                          <div className="cm-item-meta">
                            {item.size && <span>Size: {item.size}</span>}
                            {item.product?.variant?.colors && (
                              <span className="cm-color-wrap">
                                Color:
                                <span
                                  className="cm-color-dot"
                                  style={{
                                    backgroundColor:
                                      item.product.variant.colors.split(",")[
                                        item.color
                                      ] ?? "transparent",
                                  }}
                                />
                              </span>
                            )}
                            <span>Qty: {item.quantity}</span>
                          </div>
                        </div>
                        <p className="cm-item-price">
                          Rs {(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="cm-section">
                <h3 className="cm-section-title">Order Summary</h3>
                <div className="cm-summary-box">
                  <div className="cm-summary-row">
                    <span>Subtotal</span>
                    <span>Rs {subtotal.toLocaleString()}</span>
                  </div>
                  <div className="cm-summary-row">
                    <span>Shipping Fee</span>
                    <span>Rs {shippingFee}</span>
                  </div>
                  {discount > 0 && (
                    <div className="cm-summary-row cm-discount">
                      <span>Discount</span>
                      <span>- Rs {discount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="cm-summary-divider" />
                  <div className="cm-summary-row cm-total">
                    <span>Total Amount</span>
                    <span>Rs {total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="cm-section">
                <h3 className="cm-section-title">Payment Method</h3>
                <div className="cm-cod-card">
                  <div className="cm-cod-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M2 8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1H2V8Zm0 3h20v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6Zm3 3a1 1 0 0 0 0 2h4a1 1 0 0 0 0-2H5Z"
                        fill="#fff"
                      />
                    </svg>
                  </div>
                  <div className="cm-cod-info">
                    <p className="cm-cod-title">Cash on Delivery</p>
                    <p className="cm-cod-desc">
                      Pay in cash when your order arrives
                    </p>
                  </div>
                  <div className="cm-cod-check">✓</div>
                </div>
                <p className="cm-cod-note">
                  Online payment is not available. Only Cash on Delivery is
                  accepted.
                </p>
              </div>

              {/* Delivery Address section — ref is used so the modal can
                  auto-scroll here whenever validation fails on submit */}
              <div className="cm-section" ref={addressSectionRef}>
                <h3 className="cm-section-title">Delivery Address</h3>
                <div className="cm-addr-grid">
                  <div className="cm-field">
                    <label>Full Name</label>
                    <input
                      ref={nameInputRef}
                      name="name"
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      placeholder="e.g. Ali Hassan"
                      className={addressErrors.name ? "cm-input-err" : ""}
                    />
                    {addressErrors.name && (
                      <span className="cm-err">{addressErrors.name}</span>
                    )}
                  </div>
                  <div className="cm-field">
                    <label>Phone Number</label>
                    <input
                      name="phone"
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      placeholder="03XX-XXXXXXX"
                      className={addressErrors.phone ? "cm-input-err" : ""}
                    />
                    {addressErrors.phone && (
                      <span className="cm-err">{addressErrors.phone}</span>
                    )}
                  </div>
                  <div className="cm-field cm-field--full">
                    <label>Street / Area</label>
                    <input
                      name="area"
                      value={address}
                      onChange={(event) => setAddress(event.target.value)}
                      placeholder="Street no, Block, Colony..."
                      className={addressErrors.area ? "cm-input-err" : ""}
                    />
                    {addressErrors.area && (
                      <span className="cm-err">{addressErrors.area}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="cm-footer">
              <button
                className="cm-cancel-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="cm-pay-btn"
                disabled={isPlacingOrder}
                onClick={createOrder}
              >
                {isPlacingOrder ? "Placing Order..." : "Proceed to Checkout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddToCart;